import {
  type AmortListResponse,
  ContractLoanTypeEnum,
  InterestRateStatus,
} from '@kk/api/modules/customerPortal';
import { formatDate } from '@kk/shared/utils/date';
import Table, {
  formatCellCurrency,
  getRowYearClassName,
} from '@kk/ui/components/Table';
import {
  type CellContext,
  type ColumnDef,
  createColumnHelper,
  type Row,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const columnHelper = createColumnHelper<AmortListResponse>();

export interface AmortListTableProps {
  data?: AmortListResponse[] | null;
  className?: string;
  loanProduct: ContractLoanTypeEnum | undefined;
  hideFirstRow?: boolean;
}

export function AmortListTable({
  data,
  className,
  loanProduct,
  hideFirstRow = true,
}: AmortListTableProps) {
  const { t } = useTranslation(['ui']);

  const formatCellToInterestValue = useCallback(
    (info: CellContext<AmortListResponse, number>) => {
      const { interestRateStatus } = info.row.original;
      const cellCurrency = formatCellCurrency(info, {
        hideFirstRow: hideFirstRow,
        emptyCellString: data ? '' : '—',
      });

      if (loanProduct === ContractLoanTypeEnum.KKvar) {
        switch (interestRateStatus) {
          case InterestRateStatus.Fixed:
            return cellCurrency;
          case InterestRateStatus.Estimated:
            return cellCurrency;
          case InterestRateStatus.BeforeInterestConditionExpiryDate:
            return t('standalone.determineAtTerm');
          case InterestRateStatus.AfterInterestConditionExpiryDate:
            return t('standalone.determineAtTerm');
          default:
            return cellCurrency;
        }
      }

      if (loanProduct === ContractLoanTypeEnum.KKfast) {
        switch (interestRateStatus) {
          case InterestRateStatus.Fixed:
            return cellCurrency;
          case InterestRateStatus.Estimated:
            return cellCurrency;
          case InterestRateStatus.BeforeInterestConditionExpiryDate:
            return t('standalone.newInterestAgreement');
          case InterestRateStatus.AfterInterestConditionExpiryDate:
            return t('standalone.newInterestAgreement');
          default:
            return cellCurrency;
        }
      }

      if (loanProduct === ContractLoanTypeEnum.KKcibor) {
        switch (interestRateStatus) {
          case InterestRateStatus.Fixed:
            return cellCurrency;
          case InterestRateStatus.Estimated:
            return cellCurrency;
          case InterestRateStatus.BeforeInterestConditionExpiryDate:
            return t('standalone.determineAtTerm');
          case InterestRateStatus.AfterInterestConditionExpiryDate:
            return t('standalone.newInterestAgreement');
          default:
            return cellCurrency;
        }
      }

      return cellCurrency;
    },
    [t, loanProduct, data, hideFirstRow],
  );

  const columns = useMemo<ColumnDef<AmortListResponse>[]>(
    () => [
      columnHelper.accessor('valueDate', {
        header: () => t('data.valueDate'),
        size: 150,
        cell: (info) => {
          return formatDate(info.getValue());
        },
      }),
      columnHelper.accessor('repayment', {
        header: () => t('data.repayment'),
        cell: (info) =>
          formatCellCurrency(info, { hideFirstRow: hideFirstRow }),
        meta: {
          rightAlign: true,
        },
      }),
      columnHelper.accessor('interest', {
        header: () => t('data.interest'),
        cell: formatCellToInterestValue,
        meta: {
          rightAlign: true,
        },
      }),
      columnHelper.accessor('margin', {
        header: () => t('data.margin'),
        cell: (info) =>
          formatCellCurrency(info, { hideFirstRow: hideFirstRow }),
        meta: {
          rightAlign: true,
        },
      }),
      columnHelper.accessor('payment', {
        header: () => t('data.payment'),
        cell: formatCellToInterestValue,
        meta: {
          rightAlign: true,
        },
      }),
      columnHelper.accessor('notionalAmount', {
        header: () => t('data.notionalAmount'),
        cell: formatCellCurrency,
        meta: {
          rightAlign: true,
        },
      }),
    ],
    [t, formatCellToInterestValue, hideFirstRow],
  );

  const getRowClassName = useCallback(
    (row: Row<AmortListResponse>) => {
      if (!data) return '';
      const firstYearInData = data[0]?.valueDate
        ? new Date(data[0].valueDate).getFullYear()
        : 0;
      const value: string = row.getValue('valueDate');
      const bg = getRowYearClassName(value, firstYearInData);

      return bg;
    },
    [data],
  );

  return (
    <div className={clsx('grid', className)}>
      <Table<AmortListResponse>
        stickyHeader
        data={data || []}
        className={clsx(
          'border-neutral-10 content-visibility-auto screen:max-h-[992px] overflow-y-auto rounded-md border',
          'print:max-h-none print:overflow-visible print:text-xs',
        )}
        size="relaxed"
        columns={columns}
        getRowClassName={getRowClassName}
      />
    </div>
  );
}

export default AmortListTable;
